import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "../lib/api";
import { Loader2, RefreshCw, Wallet } from "lucide-react";
import { toast } from "sonner";

export function RecipientConnectSetup({ compact = false }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async (sync = false) => {
        setLoading(true);
        try {
            const { data } = sync
                ? await api.post("/me/connect/sync")
                : await api.get("/me/connect/status");
            setStatus(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not load payout setup"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const params = new URLSearchParams(window.location.search);
        if (params.get("connect") === "return" || params.get("connect") === "refresh") {
            load(true);
        }
    }, [load]);

    const startOnboard = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/me/connect/onboard", {
                origin_url: `${window.location.origin}/settings?tab=profile`,
            });
            if (data?.url) window.location.href = data.url;
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not start payout setup"));
        } finally {
            setBusy(false);
        }
    };

    const ready = status?.ready;
    const phase = status?.phase || "not_started";

    if (loading && !status) {
        return (
            <div className={`rounded-lg border border-white/[0.08] bg-white/[0.02] ${compact ? "p-3" : "p-4"}`}>
                <p className="text-sm text-white/45">Loading payout setup…</p>
            </div>
        );
    }

    return (
        <div
            className={`rounded-lg border ${ready ? "border-[#00BF0D]/25 bg-[#00BF0D]/[0.04]" : "border-white/[0.08] bg-white/[0.02]"} ${compact ? "p-3" : "p-4"}`}
            data-testid="recipient-connect-setup"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="label-eyebrow inline-flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5 text-[#00BF0D]/80" />
                        Payout setup
                    </p>
                    <p className={`mt-1 text-sm ${ready ? "text-[#00BF0D]/90" : "text-white/70"}`}>
                        {status?.message || "Connect Stripe to receive your share of pool payouts."}
                    </p>
                    {!ready && phase !== "not_started" && (status?.requirements_due_labels || []).length > 0 && (
                        <p className="mt-1.5 text-xs text-white/45">
                            Needed: {status.requirements_due_labels.slice(0, 3).join(", ")}
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        onClick={() => load(true)}
                        className="btn-ghost rounded-md p-2"
                        title="Refresh status"
                        data-testid="recipient-connect-refresh"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                    {!ready && (
                        <button
                            type="button"
                            onClick={startOnboard}
                            disabled={busy || !status?.configured}
                            className="btn-neon rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="recipient-connect-start"
                        >
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {phase === "not_started" ? "Set up payouts" : "Continue setup"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
